import * as XLSX from "xlsx";

export interface QuotationImportHeaders {
  requirementItemId: string;
  productCode: string;
  productName: string;
  supplierCode: string;
  supplierName: string;
  supplierPrice: string;
  mpq: string;
  remark: string;
}

export interface RawQuotationImportRow {
  rowNumber: number;
  requirementItemId: string;
  productCode: string;
  productName: string;
  supplierCode: string;
  supplierName: string;
  supplierPrice: string;
  mpq: string;
  remark: string;
}

// 安全地获取单元格的值，避免 undefined 或 null 的情况
const getCellValue = (row: unknown[], index: number): string => {
  const value = row[index];
  return value === undefined || value === null ? "" : String(value).trim();
};

// 解析导入的报价 Excel 文件
export const parseQuotationImportFile = (
  file: File,
  headers: QuotationImportHeaders
): Promise<RawQuotationImportRow[]> => new Promise((resolve, reject) => {
  const reader = new FileReader();

  reader.onload = event => {
    try {
      const workbook = XLSX.read(event.target?.result, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      if (!worksheet) {
        reject(new Error("empty-file"));
        return;
      }

      const rows = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
        header: 1,
        defval: ""
      });
      if (rows.length < 2) {
        reject(new Error("empty-file"));
        return;
      }

      const headerRow = rows[0].map(value => String(value ?? "").trim());
      const expectedHeaders = Object.values(headers);
      // 表头来自当前页面导出的本地化字段，缺少任一必需列都拒绝继续解析。
      if (expectedHeaders.some(header => !headerRow.includes(header))) {
        reject(new Error("invalid-header"));
        return;
      }

      const headerIndexes = Object.fromEntries(
        Object.entries(headers).map(([key, header]) => [key, headerRow.indexOf(header)])
      ) as Record<keyof QuotationImportHeaders, number>;

      const result = rows.slice(1).reduce<RawQuotationImportRow[]>((list, row, index) => {
        // 跳过完全空白的 Excel 行，同时保留原始行号用于错误定位。
        if (!row.some(value => String(value ?? "").trim() !== "")) return list;

        list.push({
          rowNumber: index + 2,
          requirementItemId: getCellValue(row, headerIndexes.requirementItemId),
          productCode: getCellValue(row, headerIndexes.productCode),
          productName: getCellValue(row, headerIndexes.productName),
          supplierCode: getCellValue(row, headerIndexes.supplierCode),
          supplierName: getCellValue(row, headerIndexes.supplierName),
          supplierPrice: getCellValue(row, headerIndexes.supplierPrice),
          mpq: getCellValue(row, headerIndexes.mpq),
          remark: getCellValue(row, headerIndexes.remark)
        });
        return list;
      }, []);

      if (result.length === 0) {
        reject(new Error("empty-file"));
        return;
      }
      resolve(result);
    } catch {
      reject(new Error("parse-failed"));
    }
  };

  reader.onerror = () => reject(new Error("parse-failed"));
  reader.readAsArrayBuffer(file);
});
